/**
 * Datas e fuso horário. Fonte: ADR-007 (docs/16) e docs/07 §4.
 *
 * Banco:    TEXT com ISO-8601 UTC e milissegundos — "2026-08-12T22:00:00.000Z"
 * Exibição: America/Sao_Paulo, via Intl (nunca offset fixo).
 *
 * Proibido formatar data ad hoc em qualquer outro lugar do código.
 */

export const APP_TIMEZONE = "America/Sao_Paulo";

/** O agora, no formato canônico do banco. */
export function nowIso(): string {
  return new Date().toISOString();
}

export function toIso(date: Date): string {
  return date.toISOString();
}

export function plusSeconds(seconds: number, from: Date = new Date()): string {
  return new Date(from.getTime() + seconds * 1000).toISOString();
}

export function plusDays(days: number, from: Date = new Date()): string {
  return plusSeconds(days * 86_400, from);
}

/** Minutos decorridos desde `iso`. Negativo se `iso` está no futuro. */
export function minutesSince(iso: string, now: Date = new Date()): number {
  return (now.getTime() - Date.parse(iso)) / 60_000;
}

export function daysUntil(iso: string, now: Date = new Date()): number {
  return Math.floor((Date.parse(iso) - now.getTime()) / 86_400_000);
}

const dateTimeFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: APP_TIMEZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const dateFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: APP_TIMEZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const timeFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: APP_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
});

/** "12/08/2026 19:00" */
export function formatDateTime(iso: string): string {
  return dateTimeFormat.format(new Date(iso)).replace(",", "");
}

/** "12/08/2026" */
export function formatDate(iso: string): string {
  return dateFormat.format(new Date(iso));
}

/** "19:00" */
export function formatTime(iso: string): string {
  return timeFormat.format(new Date(iso));
}

/**
 * Converte data e hora informadas pelo usuário (em horário de Brasília) para
 * o ISO-8601 UTC que vai ao banco.
 *
 * Usa Intl para descobrir o offset vigente naquela data, em vez de assumir -03:00 —
 * o Brasil não tem horário de verão hoje, mas essa regra já mudou antes.
 */
export function localInputToUtcIso(dateInput: string, timeInput: string): string {
  const [year, month, day] = dateInput.split("-").map(Number);
  const [hour, minute] = timeInput.split(":").map(Number);

  if (
    year === undefined || month === undefined || day === undefined ||
    hour === undefined || minute === undefined
  ) {
    throw new Error(`Data ou hora inválida: "${dateInput}" "${timeInput}"`);
  }

  // Primeira aproximação: trata os valores como se já fossem UTC.
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  // Descobre quanto essa instância representa no fuso do app e corrige a diferença.
  const offsetMs = guess - zonedTimeToUtcMs(new Date(guess));
  return new Date(guess + offsetMs).toISOString();
}

/** Quantos milissegundos a data `d` representa quando lida no fuso do app. */
function zonedTimeToUtcMs(d: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);

  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);

  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
}

/** Separa um ISO-8601 UTC nos valores de `<input type="date">` e `<input type="time">`. */
export function utcIsoToLocalInputs(iso: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "00";

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour") === "24" ? "00" : get("hour")}:${get("minute")}`,
  };
}
